import React from "react";
import { Repeat, ArrowDown } from "lucide-react";
import { FormRunner } from "@/components/forms/FormRunner";
import { US_STATES } from "@/data/site";

const num = (x) => parseFloat(x) || 0;

export default function TransactionalFunding() {
  const fields = [
    { name: "applicant_name", label: "Applicant Name", required: true },
    { name: "company", label: "Company" },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "phone", label: "Phone" },
    { name: "property_address", label: "Property Address", full: true },
    { name: "property_state", label: "Property State", type: "select", options: US_STATES },
    { name: "ab_price", label: "A-B Purchase Price", type: "number", required: true },
    { name: "bc_price", label: "B-C Sale Price", type: "number", required: true },
    { name: "closing_date", label: "Closing Date", type: "date" },
    { name: "buyer_entity", label: "Buyer Entity" },
    { name: "seller", label: "Seller" },
    { name: "end_buyer", label: "End Buyer" },
    { name: "title_company", label: "Title Company" },
    { name: "end_buyer_funds_source", label: "Source of End Buyer Funds" },
    { name: "notes", label: "Notes", type: "textarea" },
  ];
  const uploads = [
    { key: "ab", label: "Upload A-B Contract", category: "Purchase Agreement" },
    { key: "bc", label: "Upload B-C Contract", category: "Purchase Agreement" },
    { key: "settlement", label: "Upload Settlement Statement (if available)", category: "Settlement Statement" },
    { key: "pof", label: "Upload Proof of End Buyer Funds (if available)", category: "Proof of Funds" },
  ];

  const compute = (form) => {
    const spread = num(form.bc_price) - num(form.ab_price);
    return [{ label: "Gross Spread (B-C − A-B)", value: spread, store: "gross_spread", display: `$${spread.toLocaleString()}` }];
  };

  return (
    <div>
      <section className="relative border-b border-edge overflow-hidden">
        <div className="absolute -top-32 left-0 w-[500px] h-[500px] rounded-full bg-brandred/10 blur-[120px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-brandred/40 bg-brandred/5 px-4 py-1.5 mb-5">
            <Repeat className="w-4 h-4 text-brandred" /><span className="eyebrow" style={{ color: "#DC2626" }}>Transactional Funding</span>
          </div>
          <h1 className="h-display text-4xl sm:text-5xl lg:text-6xl text-white max-w-3xl">Capital built for the closing table.</h1>
          <p className="mt-5 text-base sm:text-lg text-dim max-w-2xl leading-relaxed">
            Short-duration capital for qualified wholesale and real estate investment transactions, including back-to-back and double closings.
          </p>

          <div className="mt-10 max-w-md">
            <FlowDiagram />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="mb-8">
          <div className="eyebrow mb-2">Get Transactional Funding</div>
          <h2 className="h-display text-2xl sm:text-3xl text-white">Transactional Funding Application</h2>
          <p className="mt-2 text-sm text-dim">For A-B / B-C closings, double closes, and wholesale transactions where a verified exit exists.</p>
        </div>
        <FormRunner
          type="transactional"
          fields={fields}
          uploads={uploads}
          compute={compute}
          submitLabel="Submit Transactional Request"
          testIdPrefix="txn"
          successMessage="Your transactional funding request has been submitted for review."
        />
      </section>
    </div>
  );
}

function FlowDiagram() {
  const steps = ["Seller", "A-B Closing", "Investor / Wholesaler", "B-C Closing", "End Buyer"];
  return (
    <div className="rounded-2xl border border-edge bg-surface/50 p-6" data-testid="txn-flow-diagram">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`rounded-lg px-4 py-3 text-center font-semibold ${i === 2 ? "bg-gold/15 text-gold border border-gold/40" : "bg-charcoal text-white border border-edge"}`}>
            {s}
          </div>
          {i < steps.length - 1 && <div className="flex justify-center py-1.5"><ArrowDown className="w-4 h-4 text-gold" /></div>}
        </React.Fragment>
      ))}
    </div>
  );
}
